import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { skusApi, requisitionsApi, ebosApi } from '../api';
import PageHeader from '../components/PageHeader';
import BulkImportRequisitionsModal from '../components/BulkImportRequisitionsModal';
import RejectionsDialog from '../components/RejectionsDialog';
import { CartValidationHead, CartValidationCells } from '../components/CartValidation';
import { Search, Plus, Lock, Loader2, X, ShieldCheck, ShoppingBag, History, Upload } from 'lucide-react';

const MIN_QUERY_LEN = 1;
const MAX_SUGGESTIONS = 12;

/** Convert YYYY-MM-DD (from <input type="date">) to DD/MM/YYYY (backend format). */
const toDDMMYYYY = (iso) => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

export default function RaiseRequisition() {
  const { user, theme } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSuper = user?.role === 'SuperAdmin';

  const [skus, setSkus] = useState([]);
  const [cooldown, setCooldown] = useState({});
  const [ebos, setEbos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [query, setQuery] = useState('');
  // iter37 — category / sub-category filter above the search box. Both are
  // client-side; the SKU catalogue is already fully loaded on mount.
  const [catFilter, setCatFilter] = useState('');
  const [subCatFilter, setSubCatFilter] = useState('');
  const categoryOptions = useMemo(() => {
    const s = new Set(skus.map(x => x.category).filter(Boolean));
    return Array.from(s).sort();
  }, [skus]);
  const subCategoryOptions = useMemo(() => {
    const s = new Set(
      skus.filter(x => !catFilter || x.category === catFilter)
          .map(x => x.subCategory).filter(Boolean),
    );
    return Array.from(s).sort();
  }, [skus, catFilter]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  // Cart entries: {sku, product, category, subCategory, mrp, pack, condition}
  // Condition is a plain marker (new / empty / partial) — captured for
  // reporting but no longer requires a photo attachment.
  const [cart, setCart] = useState([]);
  const [error, setError] = useState('');
  const [rejectState, setRejectState] = useState(null);
  const [lineResults, setLineResults] = useState({});
  const [branchLabel, setBranchLabel] = useState('');
  const [checking, setChecking] = useState(false);
  const idemKey = useRef(null);
  // Requisition-level mandatory justification (why this tester is being asked for).
  const [remarks, setRemarks] = useState('');
  const inputRef = useRef(null);
  const suggestBoxRef = useRef(null);

  // SA-only backdated-historical entry state. Auto-open when navigated
  // from the sidebar "Backfill historical" link (?backfill=1).
  const [backdateOn, setBackdateOn] = useState(() => searchParams.get('backfill') === '1' && isSuper);
  const [backdate, setBackdate] = useState('');       // YYYY-MM-DD from <input type=date>
  const [backdateEbo, setBackdateEbo] = useState('');  // eboBranchCode
  const [bulkOpen, setBulkOpen] = useState(false);
  // iter39 — SA raises a LIVE requisition on behalf of any EBO.
  const [onBehalfEbo, setOnBehalfEbo] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const promises = [skusApi.list()];
        // Non-SA users need cooldown; SA loading EBO list for backdate picker
        if (isSuper) promises.push(ebosApi.list());
        else promises.push(requisitionsApi.cooldown());
        const [s, other] = await Promise.all(promises);
        setSkus(s || []);
        if (isSuper) setEbos(other || []);
        else setCooldown(other || {});
      } finally { setLoading(false); }
    })();
  }, [isSuper]);

  useEffect(() => {
    if (!isSuper) return;
    if (!onBehalfEbo) { setCooldown({}); return; }
    requisitionsApi.cooldown(onBehalfEbo).then(c => setCooldown(c || {})).catch(() => setCooldown({}));
  }, [isSuper, onBehalfEbo]);

  useEffect(() => {
    idemKey.current = null;
    if (backdateOn || cart.length === 0 || (isSuper && !onBehalfEbo)) { setLineResults({}); return undefined; }
    setChecking(true);
    let stale = false;
    const payload = { lines: cart.map(c => ({ sku: c.sku, qty: 1, condition: c.condition || 'empty' })), ...(isSuper ? { onBehalfEboBranchCode: onBehalfEbo } : {}) };
    const t = setTimeout(() => {
      requisitionsApi.validate(payload)
        .then(r => { if (!stale) { setLineResults(Object.fromEntries(r.lineResults.map(x => [x.sku, x]))); setBranchLabel(`${r.branchName} (${r.branchCode})`); } })
        .catch(() => { if (!stale) setLineResults({}); })
        .finally(() => { if (!stale) setChecking(false); });
    }, 350);
    return () => { stale = true; clearTimeout(t); };
  }, [cart, onBehalfEbo, backdateOn, isSuper]);

  // In backdate mode we ignore the cooldown map — the entry is historical.
  const isLocked = (sku) => (backdateOn ? false : !!cooldown[sku]?.locked);

  const skuEligibility = (s) => {
    if (isLocked(s.sku)) {
      const c = cooldown[s.sku];
      return { ok: false, reason: c?.nextEligibleDate ? `Cooling period active · next eligible ${c.nextEligibleDate}` : 'Cooling period active' };
    }
    return { ok: true };
  };

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    // iter37 — when the user has picked a category or sub-category filter,
    // suggestions render even with an empty search input (browsing the
    // filtered catalogue is the primary UX). Otherwise keep the min-query-length gate.
    const filteringByFacet = !!(catFilter || subCatFilter);
    if (!filteringByFacet && q.length < MIN_QUERY_LEN) return [];
    const cartCodes = new Set(cart.map(c => c.sku));
    const matches = [];
    for (const s of skus) {
      if (cartCodes.has(s.sku)) continue; // already added
      if (catFilter && s.category !== catFilter) continue;
      if (subCatFilter && s.subCategory !== subCatFilter) continue;
      const skuMatch = q ? s.sku.toLowerCase().includes(q) : true;
      const productMatch = q ? s.product.toLowerCase().includes(q) : true;
      const catMatch = q ? ((s.category || '').toLowerCase().includes(q) || (s.subCategory || '').toLowerCase().includes(q)) : true;
      if (skuMatch || productMatch || catMatch) {
        const rank = q
          ? (s.sku.toLowerCase().startsWith(q) ? 0 : s.product.toLowerCase().startsWith(q) ? 1 : 2)
          : 0;
        const { ok } = skuEligibility(s);
        matches.push({ s, rank, eligibleRank: ok ? 0 : 1 });
        if (matches.length >= MAX_SUGGESTIONS * 3) break;
      }
    }
    matches.sort((a, b) => a.eligibleRank - b.eligibleRank || a.rank - b.rank || a.s.sku.localeCompare(b.s.sku));
    return matches.slice(0, MAX_SUGGESTIONS).map(m => m.s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, catFilter, subCatFilter, skus, cart, cooldown]);

  useEffect(() => { setActiveIdx(0); }, [query]);

  const pick = (s) => {
    setError('');
    const { ok, reason } = skuEligibility(s);
    if (!ok) {
      setError(`${s.sku} — ${s.product}: ${reason}`);
      return;
    }
    if (cart.some(c => c.sku === s.sku)) return;
    setCart(prev => [...prev, { ...s, condition: 'empty' }]);
    setQuery('');
    setShowSuggestions(false);
    // Return focus to the input so the user can immediately add another SKU
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const removeFromCart = (sku) => {
    // iter37 — reconfirm before dropping a line so a stray click doesn't
    // wipe an SKU the user painstakingly picked.
    if (!window.confirm(`Remove "${sku}" from this requisition?`)) return;
    setCart(prev => prev.filter(c => c.sku !== sku));
  };

  const changeCondition = (sku, condition) => {
    setCart(prev => prev.map(c => (c.sku === sku ? { ...c, condition } : c)));
  };

  const submit = async () => {
    if (cart.length === 0) return;
    if (backdateOn) {
      if (!backdate) { setError('Pick the historical delivery date'); return; }
      if (!backdateEbo) { setError('Pick the EBO branch this old tester was given to'); return; }
    } else if (isSuper && !onBehalfEbo) {
      // iter39 — SA must pick a target EBO for live requisitions (SA has no branch of their own).
      setError('Pick the EBO branch you are raising this requisition on behalf of'); return;
    }
    // iter37 — remarks are optional; drop the client-side required-field guard.
    setSubmitting(true); setError('');
    try {
      const lines = cart.map(c => ({
        sku: c.sku,
        qty: 1,
        condition: c.condition || 'empty',
      }));
      let payload;
      if (backdateOn) {
        payload = { lines, backdate: toDDMMYYYY(backdate), eboBranchCode: backdateEbo };
      } else if (isSuper) {
        payload = { lines, remarks: remarks.trim(), onBehalfEboBranchCode: onBehalfEbo };
      } else {
        payload = { lines, remarks: remarks.trim() };
      }
      if (!backdateOn) {
        const check = await requisitionsApi.validate(payload);
        if (check.rejectedLines.length > 0) {
          setRejectState({ rejections: check.rejectedLines, passCount: check.acceptedSkus.length, payload, results: check.lineResults });
          return;
        }
        await createAndOpen(payload, check.lineResults);
        return;
      }
      await createAndOpen(payload);
    } catch (e) {
      handleSubmitError(e);
    } finally { setSubmitting(false); }
  };

  const handleSubmitError = (e) => {
    if (e.response && e.response.status < 500) idemKey.current = null;
    const detail = e.response?.data?.detail;
    if (detail && typeof detail === 'object' && detail.rejectedLines) {
      setRejectState({ rejections: detail.rejectedLines, passCount: 0, payload: null });
    } else {
      setError(typeof detail === 'string' ? detail : 'Failed to submit requisition');
    }
  };

  const createAndOpen = async (payload, results) => {
    if (!idemKey.current) idemKey.current = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
    const req = await requisitionsApi.create(payload, idemKey.current);
    idemKey.current = null;
    setCart([]); setRemarks(''); setOnBehalfEbo('');
    navigate(`/app/requisitions/${encodeURIComponent(req.id)}`, { state: { justCreated: true } });
  };

  const confirmPartial = async () => {
    const { payload, results } = rejectState;
    setRejectState(null); setSubmitting(true);
    try { await createAndOpen(payload, results); }
    catch (e) { handleSubmitError(e); }
    finally { setSubmitting(false); }
  };

  const onKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(suggestions[activeIdx]); }
    else if (e.key === 'Escape') { setShowSuggestions(false); }
  };

  // Close suggestion box when clicking outside
  useEffect(() => {
    const onDoc = (ev) => {
      if (suggestBoxRef.current && !suggestBoxRef.current.contains(ev.target) && ev.target !== inputRef.current) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (loading) {
    return <div className="p-10 text-center text-[#7a6f5c]"><Loader2 className="animate-spin inline" /></div>;
  }

  return (
    <div>
      <PageHeader
        title="Raise a tester requisition"
        subtitle={<span>Start typing a SKU code, product name, or category. Each Tester SKU passes three checks before it is accepted: its cooling period since the last delivery to your branch, your branch stock, and Head Office stock. Lines that fail are listed with the reason. {skus.length} SKU(s) in the catalogue.</span>}
      />

      {isSuper && !backdateOn && (
        <div className="fm-card p-4 mb-4" data-testid="sa-on-behalf-panel" style={{ borderTop: `2px solid ${theme.primary}` }}>
          <label className="text-[13.5px] font-semibold flex items-center gap-2" htmlFor="sa-on-behalf-ebo">
            <ShieldCheck size={14} style={{ color: theme.primary }} />
            Raise this requisition on behalf of
          </label>
          <p className="text-[11.5px] text-[#7a6f5c] mt-1 mb-2">
            SuperAdmin doesn't own a branch — pick the EBO this requisition is for. The requisition follows the normal live workflow; the EBO and you both receive the confirmation email.
          </p>
          <select
            id="sa-on-behalf-ebo"
            data-testid="sa-on-behalf-ebo"
            value={onBehalfEbo}
            onChange={(e) => setOnBehalfEbo(e.target.value)}
            className="fm-select fm-focus w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white max-w-[520px]"
          >
            <option value="">Select EBO branch…</option>
            {ebos.filter(e => (e.status || 'Active') === 'Active').map(e => (
              <option key={e.code} value={e.code}>{e.code} · {e.name}{e.brand ? ` · ${e.brand}` : ''}</option>
            ))}
          </select>
        </div>
      )}

      {isSuper && (
        <div className="fm-card p-4 mb-4" data-testid="sa-backdate-panel" style={{ borderTop: `2px solid ${theme.primary}` }}>
          <div className="flex items-center justify-between">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="sa-backdate-toggle"
                checked={backdateOn}
                onChange={e => setBackdateOn(e.target.checked)}
                className="fm-focus w-4 h-4 accent-[--fm-primary]"
                style={{ accentColor: theme.primary }}
              />
              <History size={14} className="text-[#7a6f5c]" />
              <span className="font-semibold text-[13.5px]">Backdate this requisition (historical entry &gt; 90 days ago)</span>
            </label>
            <button
              type="button"
              data-testid="sa-bulk-historical-btn"
              onClick={() => setBulkOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[12.5px] font-medium border border-[#E7E2D3] bg-white hover:bg-[#F7F4EA]"
            >
              <Upload size={13} /> Bulk import historical
            </button>
          </div>
          {backdateOn && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-[12.5px] font-medium block mb-1">Delivery date (DD/MM/YYYY)</label>
                <input
                  type="date"
                  data-testid="sa-backdate-date"
                  value={backdate}
                  onChange={e => setBackdate(e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                  className="fm-focus w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white"
                />
              </div>
              <div>
                <label className="text-[12.5px] font-medium block mb-1">EBO branch that received it</label>
                <select
                  data-testid="sa-backdate-ebo"
                  value={backdateEbo}
                  onChange={e => setBackdateEbo(e.target.value)}
                  className="fm-select fm-focus w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white"
                >
                  <option value="">Select branch…</option>
                  {ebos.map(e => (
                    <option key={e.code} value={e.code}>{e.code} · {e.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2 text-[12px] text-[#7a6f5c]">
                Cooldown is skipped and all lines land as <span className="font-semibold text-[#2f6b41]">Delivered</span>. No warehouse email is fired — this is a historical backfill of testers issued before the portal.
              </div>
            </div>
          )}
        </div>
      )}

      {/* SKU search-picker */}
      <div className="fm-card p-4 mb-4">
        <label className="block text-[13px] font-medium mb-2">Add a SKU</label>
        <div className="flex flex-wrap gap-2 mb-3">
          <select
            data-testid="req-cat-filter"
            value={catFilter}
            onChange={(e) => { setCatFilter(e.target.value); setSubCatFilter(''); setShowSuggestions(true); }}
            className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white min-w-[160px]"
          >
            <option value="">All categories</option>
            {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            data-testid="req-subcat-filter"
            value={subCatFilter}
            onChange={(e) => { setSubCatFilter(e.target.value); setShowSuggestions(true); }}
            className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white min-w-[160px]"
          >
            <option value="">All sub-categories</option>
            {subCategoryOptions.map(sc => <option key={sc} value={sc}>{sc}</option>)}
          </select>
          {(catFilter || subCatFilter) && (
            <button
              onClick={() => { setCatFilter(''); setSubCatFilter(''); }}
              className="px-2 py-2 text-[12px] text-[#7a6f5c] hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7a6f5c]" />
          <input
            ref={inputRef}
            data-testid="req-sku-search"
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={onKeyDown}
            placeholder="Type at least 1 character — e.g. LIP, mascara, FM-FAC-201…"
            className="fm-focus w-full border border-[#E7E2D3] rounded-md pl-9 pr-3 py-2.5 text-[14px] bg-white"
            autoComplete="off"
          />
          {showSuggestions && (query.trim().length >= MIN_QUERY_LEN || catFilter || subCatFilter) && (
            <div
              ref={suggestBoxRef}
              className="absolute left-0 right-0 top-full mt-1 fm-card overflow-y-auto max-h-[380px] z-20"
              data-testid="req-sku-suggestions"
            >
              {suggestions.length === 0 ? (
                <div className="px-4 py-3 text-[13px] text-[#7a6f5c]">
                  No SKUs match "<span className="font-mono">{query}</span>".
                </div>
              ) : suggestions.map((s, i) => {
                const { ok, reason } = skuEligibility(s);
                return (
                  <button
                    key={s.sku}
                    type="button"
                    data-testid={`req-sku-suggestion-${s.sku}`}
                    disabled={!ok}
                    onMouseDown={(ev) => ev.preventDefault()}
                    onClick={() => pick(s)}
                    onMouseEnter={() => setActiveIdx(i)}
                    className={`w-full text-left px-4 py-2.5 border-b border-[#EFEADC] last:border-b-0 flex items-start gap-3 transition-colors ${
                      i === activeIdx && ok ? 'bg-[#F7E7EA]' : 'hover:bg-[#F7F4EA]'
                    } ${!ok ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {ok
                        ? <ShieldCheck size={14} color="#4D7C5F" />
                        : <Lock size={14} color="#B78140" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono text-[12.5px] font-semibold" style={{ color: theme.primary }}>{s.sku}</span>
                        <span className="font-mono text-[10.5px] text-[#7a6f5c]" title="Tester SKU">·&nbsp;{s.testerSku || `${s.sku}-T`}</span>
                        <span className="text-[13.5px] truncate">{s.product}</span>
                      </div>
                      <div className="text-[11.5px] text-[#7a6f5c] mt-0.5">
                        {s.category} · {s.subCategory} · {s.pack} · ₹{s.mrp}
                        {!ok && <span className="ml-2 font-medium" style={{ color: '#B78140' }}>{reason}</span>}
                      </div>
                    </div>
                    {ok && <Plus size={14} className="text-[#7a6f5c] flex-shrink-0 mt-1" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <p className="text-[11.5px] text-[#7a6f5c] mt-2">Use ↑ ↓ to navigate suggestions, Enter to add.</p>
      </div>

      {/* Cart */}
      <div className="fm-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7E2D3]">
          <div className="flex items-center gap-2 text-[14px] font-medium">
            <ShoppingBag size={16} style={{ color: theme.primary }} />
            <span data-testid="req-cart-count">{cart.length}</span> SKU(s) selected
            {!backdateOn && branchLabel && cart.length > 0 && <span className="ml-3 text-[12px] font-normal text-[#7a6f5c]" data-testid="req-cart-branch">Ordering branch: {branchLabel}</span>}
          </div>
          {cart.length > 0 && (
            <button
              disabled={submitting || (isSuper && !backdateOn && !onBehalfEbo)}
              onClick={submit}
              data-testid="req-submit-btn"
              className="px-4 py-2 rounded-md text-white text-[13px] font-medium disabled:opacity-60"
              style={{ background: theme.primary }}
              title={isSuper && !backdateOn && !onBehalfEbo ? 'Pick the EBO branch you are raising this requisition on behalf of' : ''}
            >
              {submitting ? 'Submitting…' : `Submit requisition (${cart.length})`}
            </button>
          )}
        </div>

        {cart.length === 0 ? (
          <div className="px-5 py-12 text-center text-[#7a6f5c] text-[13.5px]">
            Selected SKUs will appear here. Add at least one to submit a requisition.
          </div>
        ) : (
          <table className="w-full text-[13.5px]" data-testid="req-cart-table">
            <thead>
              <tr className="text-left text-[12px] text-[#7a6f5c] border-b border-[#E7E2D3] whitespace-nowrap">
                <th className="px-4 py-3 font-medium">SKU</th>
                <th className="px-4 py-3 font-medium">Product</th>
                <th className="px-4 py-3 font-medium">Pack</th>
                <th className="px-4 py-3 font-medium text-right">MRP</th>
                <th className="px-4 py-3 font-medium text-center">Qty</th>
                {!backdateOn && <th className="px-4 py-3 font-medium">Condition</th>}
                {!backdateOn && <CartValidationHead />}
                <th className="px-4 py-3 font-medium text-right pr-6"></th>
              </tr>
            </thead>
            <tbody>
              {cart.map(c => (
                <tr key={c.sku} className="border-t border-[#EFEADC] align-top" data-testid={`req-cart-row-${c.sku}`}>
                  <td className="px-4 py-3 font-mono text-[13px]">{c.sku}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{c.product}</div>
                    <div className="text-[11px] text-[#7a6f5c]">{c.category} · {c.subCategory}</div>
                  </td>
                  <td className="px-4 py-3">{c.pack}</td>
                  <td className="px-4 py-3 text-right">₹{c.mrp}</td>
                  <td className="px-4 py-3 text-center">1</td>
                  {!backdateOn && (
                    <td className="px-4 py-3">
                      <select
                        data-testid={`req-cart-condition-${c.sku}`}
                        value={c.condition || 'empty'}
                        onChange={e => changeCondition(c.sku, e.target.value)}
                        className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-2 py-1 text-[12.5px] bg-white"
                        title="Marks the tester's condition for reporting"
                      >
                        <option value="empty">Empty</option>
                        <option value="damaged">Damaged</option>
                      </select>
                    </td>
                  )}
                  {!backdateOn && <CartValidationCells sku={c.sku} result={lineResults[c.sku]} checking={checking} />}
                  <td className="px-4 py-3 text-right pr-6">
                    <button
                      data-testid={`req-cart-remove-${c.sku}`}
                      onClick={() => removeFromCart(c.sku)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E7E2D3] bg-white text-[12px] hover:bg-[#F7F4EA]"
                    >
                      <X size={12} /> Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Requisition-level mandatory remarks — not shown in SA backdate mode
          because historical backfills pre-date the portal and don't need it. */}
      {!backdateOn && cart.length > 0 && (
        <div className="fm-card p-4 mt-4" data-testid="req-remarks-card">
          <label htmlFor="req-remarks" className="block text-[13px] font-medium mb-1">
            Remarks <span className="text-[#7a6f5c] font-normal text-[11px]">(optional)</span>
          </label>
          <p className="text-[11.5px] text-[#7a6f5c] mb-2">
            Optional. Add context if useful (e.g. new SKU launch, replenishment, damaged tester).
          </p>
          <textarea
            id="req-remarks"
            data-testid="req-remarks-input"
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder="Why does the branch need this tester? Provide context so the warehouse can prioritise correctly."
            className="fm-focus w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white"
          />
          <div className="flex justify-between text-[11px] text-[#7a6f5c] mt-1">
            <span></span>
            <span>{remarks.length}/1000</span>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 px-4 py-3 rounded-md bg-[#F2E2E2] text-[#a04747] text-[13px]" data-testid="req-error">
          {error}
        </div>
      )}

      <RejectionsDialog
        open={!!rejectState}
        rejections={rejectState?.rejections || []}
        passCount={rejectState?.passCount || 0}
        onClose={() => setRejectState(null)}
        onConfirm={confirmPartial}
        primary={theme.primary}
      />

      {isSuper && (
        <BulkImportRequisitionsModal
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onImported={() => { /* future: refresh a list if we ever surface historical reqs here */ }}
          ebos={ebos}
          theme={theme}
        />
      )}
    </div>
  );
}
